import { useMemo } from 'react';
import ProsemirrorRichTextEditor from 'hubspot-prosemirror/prosemirror/ProsemirrorEditor';
import { TOOLBAR_CUSTOMIZATION } from 'hubspot-prosemirror/prosemirror/editorCapabilities/constants';
import { LAYOUT_TYPES } from 'hubspot-prosemirror/prosemirror/editorCapabilities/toolbarCustomization';
import { PASTE_OVERRIDE } from 'hubspot-prosemirror/prosemirror/editorCapabilities';
import useHubSpotRTE from 'hubspot-prosemirror/prosemirror/useHubSpotRTE';
import { convertToHtml, hasContent } from 'hubspot-prosemirror/prosemirror/ProsemirrorUtils';
import { getPlaceholderPlugin } from 'hubspot-prosemirror/prosemirror/plugins/PlaceholderPlugin';
import { View } from 'foundations-components/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Button } from 'foundations-components/transitional/components/Button';
import FormattedMessage from 'I18n/components/FormattedMessage';
import styled from 'foundations-theming/utils';
import I18n from 'I18n';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const EditorFrame = styled.div.withConfig({
  displayName: "RichContentBlock__EditorFrame"
})(({
  theme
}) => ({
  '.ProsemirrorEditor__wrapper': {
    padding: '0 !important'
  },
  '.ProseMirror': {
    color: `${theme.color['text-core-default']} !important`
  }
}));
const EDITOR_CONFIG = {
  BOLD: {},
  ITALIC: {},
  UNDERLINE: {},
  LISTS: {},
  LINK: {},
  EMOJI: {},
  // strip formatting that can't be controlled directly via the editor
  [PASTE_OVERRIDE]: {
    stripTextColor: true,
    stripTextHighlights: true,
    stripTextStyle: true,
    stripTextSize: true
  },
  [TOOLBAR_CUSTOMIZATION]: ['BOLD', 'ITALIC', 'UNDERLINE', {
    layoutType: LAYOUT_TYPES.DIVIDER,
    key: 'divider-1'
  }, 'LISTS', {
    layoutType: LAYOUT_TYPES.DIVIDER,
    key: 'divider-2'
  }, 'LINK', 'EMOJI']
};
export function RichContentBlock({
  description,
  onSave,
  onCancel
}) {
  const placeholderPlugins = useMemo(() => [getPlaceholderPlugin(I18n.text('dealPlan.richContentBlock.placeholder'))], []);
  const [editorState, setEditorState] = useHubSpotRTE(description, EDITOR_CONFIG);

  // This ensures that when the user has deleted all content, we actually
  // save an empty string with no dangling html tags
  const normalizedHtml = hasContent(editorState) ? convertToHtml(editorState).trim() : '';
  const hasUnsavedChanges = normalizedHtml !== description;
  return /*#__PURE__*/_jsxs(View, {
    marginBlockEnd: "600",
    children: [/*#__PURE__*/_jsx(EditorFrame, {
      children: /*#__PURE__*/_jsx(View, {
        borderWidth: "100",
        borderStyle: "solid",
        borderColor: "border-core-default",
        borderRadius: "100",
        padding: "300",
        backgroundColor: "fill-field-default",
        children: /*#__PURE__*/_jsx(ProsemirrorRichTextEditor, {
          editorState: editorState,
          setEditorState: setEditorState,
          editorConfig: EDITOR_CONFIG,
          customPlugins: placeholderPlugins
        })
      })
    }), /*#__PURE__*/_jsxs(Flex, {
      marginBlockStart: "200",
      gap: "200",
      children: [/*#__PURE__*/_jsx(Button, {
        use: "primary",
        size: "extra-small",
        disabled: !hasUnsavedChanges,
        onClick: () => {
          void onSave(normalizedHtml);
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "dealPlan.richContentBlock.save"
        })
      }), onCancel && /*#__PURE__*/_jsx(Button, {
        use: "unstyled",
        onClick: onCancel,
        children: /*#__PURE__*/_jsx(Text, {
          use: "disabled",
          typography: "detail-100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "dealPlan.richContentBlock.cancel"
          })
        })
      })]
    })]
  });
}